

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, type Transition, type TargetAndTransition } from "framer-motion";
import { hasDismissOwner } from "@/lib/a11y/dismissOwner";
import { TooltipLayer } from "@/components/PInfo/PInfo";
import { hintZAbove } from "@/components/PInfo/tooltipLayer";
import {
  SHEET_INITIAL,
  SHEET_ANIMATE,
  SHEET_EXIT,
  SHEET_SPRING,
} from "@/components/Modal/sheetMotion";

const ANIMATION_PRESETS = {
  scale: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { type: "spring", damping: 20, stiffness: 300 } as Transition,
  },
  // Single source of truth for sheet physics is sheetMotion.ts — the hand
  // rolled sheet modals import the same constants, so both families stay in
  // sync when the spring is tuned.
  slideUp: {
    initial: SHEET_INITIAL,
    animate: SHEET_ANIMATE,
    exit: SHEET_EXIT,
    transition: SHEET_SPRING,
  },
  dropdown: {
    initial: { opacity: 0, scale: 0.95, y: -10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: -10 },
    transition: { duration: 0.15 } as Transition,
  },
} as const;

/**
 * The overlay ladder. Rungs are pinned (order + values) by
 * `src/__tests__/overlay-z-ladder.test.ts` — change one and that test tells
 * you what else has to move.
 *
 * The host/nested/nestedChild band exists because a modal that OPENS another
 * modal cannot use `high`: both portal to `document.body`, so they are
 * siblings in the root stacking context, and the helper pickers they in turn
 * open (ImagePicker z-1000, the WB dropdown portals z-9999) are already
 * above `high`. The band sits in the empty space between the builder appbars
 * (website z-100, other builders z-200 + z-250 menu + z-300 dropdowns — all
 * contained by the bar's own stacking context) and ImagePicker.
 */
const Z_INDEX_MAP = {
  base: "z-50",
  /** A modal that HOSTS other modals (ProgramSpeakerManager, PEditPage). */
  host: "z-[400]",
  /** A modal opened FROM a `host` modal (the speaker/sponsor/item editors). */
  nested: "z-[600]",
  /** A dialog opened FROM a `nested` modal (detail-layout config). */
  nestedChild: "z-[800]",
  high: "z-[1000]",
  /**
   * A dialog opened from INSIDE a fixed panel that itself sits ABOVE `high`.
   * The guest-list FILTER popup is one: its panel is `fixed z-1100`
   * (PFilter.tsx), one rung over the default modal, so a `high` dialog
   * mounted from it opens BEHIND the panel that opened it — the markup is
   * all there and only the pointer notices, which is why a component test
   * cannot see it. The views popup (PView, z-50) is the same dialog on the
   * right side of the same rung, and that asymmetry is what this token
   * removes: a dialog that can be opened from several panels asks for this
   * once instead of guessing per call site.
   */
  overPanel: "z-[1200]",
  critical: "z-[9999]",
  // Above the AI assistant drawer/rail (z-10000) but below toasts (z-10002) —
  // for modals launched from INSIDE the drawer (e.g. the bug-report form).
  aiOverlay: "z-[10001]",
  /**
   * A dialog opened from INSIDE the guest popup (its "Ankunft melden an"
   * dialog, the data-export dialog): the send dialog's opt-in list raises
   * that popup to z-[10070] (`OptInWarningBlock`), so anything lower opens
   * BEHIND it. Above toasts (z-10002) — a toast fired from such a dialog
   * shows under its backdrop, dimmed. Its hints get a layer one above it
   * (`hintZAbove`, audit hop3 r2 M1).
   */
  overGuestPopup: "z-[10080]",
} as const;

/** Exported for the ladder test. */
export type ModalZIndex = keyof typeof Z_INDEX_MAP;
export const MODAL_Z_INDEX_MAP: Record<ModalZIndex, string> = Z_INDEX_MAP;

/**
 * Tailwind only generates classes that appear VERBATIM somewhere in source.
 * The old `sm:${size}` runtime interpolation produced classes like
 * `sm:max-w-120` that never existed as literals, so the width cap silently
 * no-oped and the modal blew up to its `w-[95vw]` mobile width on desktop.
 * Every supported size therefore maps to a LITERAL class string here — add a
 * row when introducing a new size. Unknown sizes fall back to the default cap
 * (with a dev warning) instead of silently uncapping.
 * Exported for the unit test that pins every literal.
 */
export const MODAL_SIZE_CLASS_MAP: Record<string, string> = {
  "max-w-100": "sm:max-w-100",
  "max-w-110": "sm:max-w-110",
  "max-w-120": "sm:max-w-120",
  "max-w-125": "sm:max-w-125",
  "max-w-140": "sm:max-w-140",
  "max-w-160": "sm:max-w-160",
  "max-w-200": "sm:max-w-200",
  // 60rem — anything narrower squeezes a w-90 rail. Kept for callers that
  // want a wide-but-not-detail modal.
  "max-w-240": "sm:max-w-240",
  // 72rem — the two-pane DETAIL popups (programme item / speaker / sponsor
  // edit: content column + tab rail), guest-popup parity. Load-bearing, not
  // cosmetic: minus the shell padding, the gutter, the 22.5rem rail and the
  // left card's own padding, 60rem left the content column at ~31rem — under
  // the 42rem that the 2-column GROUP grid needs, so the HALF group width
  // three layout builders offer could never render.
  "max-w-288": "sm:max-w-288",
  "max-w-md": "sm:max-w-md",
  "max-w-lg": "sm:max-w-lg",
  "max-w-xl": "sm:max-w-xl",
  "max-w-3xl": "sm:max-w-3xl",
};

export const DEFAULT_MODAL_SIZE = "max-w-125";

interface ModalWrapperProps {
  children: React.ReactNode;
  onClose: () => void;
  animation?: keyof typeof ANIMATION_PRESETS;
  zIndex?: keyof typeof Z_INDEX_MAP;
  size?: string;
  mobileWidth?: string;
  backdropClosable?: boolean;
  className?: string;
  /**
   * "sheet": below the sm breakpoint the modal docks to the bottom edge as a
   * full-width bottom sheet (guestlist pattern); sm+ keeps the legacy centered
   * layout and width (`w-[95vw]` capped by `size`). Sheet mode uses the
   * slideUp preset on ALL breakpoints — the consumer's `animation` and
   * `mobileWidth` props are ignored. Default "center" keeps the legacy
   * centered behavior everywhere.
   */
  mobile?: "center" | "sheet";
}

export const ModalWrapper = ({
  children,
  onClose,
  animation = "scale",
  // Default ABOVE app chrome: builder appbars are sticky z-200 (overflow menu
  // z-250, a tool dropdown z-300), so the old z-50 default rendered modals
  // UNDER the bars. z-[1000] clears all chrome while staying below toasts
  // (z-10002) and tooltips (z-9999). Pass zIndex="base" for the rare modal that
  // must sit lower.
  zIndex = "high",
  size = DEFAULT_MODAL_SIZE,
  mobileWidth = "w-[95vw]",
  backdropClosable = true,
  className = "",
  mobile = "center",
}: ModalWrapperProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Sheet mode always animates as slideUp — a scale-in panel that is bottom-
  // docked on phones looks broken, and the sm+ difference is cosmetic only.
  const preset = ANIMATION_PRESETS[mobile === "sheet" ? "slideUp" : animation];
  const zClass = Z_INDEX_MAP[zIndex];
  // Literal-class lookup (see MODAL_SIZE_CLASS_MAP): never interpolate the
  // size into a class name at runtime — Tailwind can't generate that.
  const smSizeClass =
    MODAL_SIZE_CLASS_MAP[size] ?? MODAL_SIZE_CLASS_MAP[DEFAULT_MODAL_SIZE];
  if (!MODAL_SIZE_CLASS_MAP[size] && !import.meta.env.PROD) {
    console.warn(
      `ModalWrapper: unknown size "${size}" — falling back to ${DEFAULT_MODAL_SIZE}. ` +
        "Add a literal entry to MODAL_SIZE_CLASS_MAP for new sizes.",
    );
  }
  const alignClass =
    mobile === "sheet" ? "items-end sm:items-center" : "items-center";
  // Sheet keeps the legacy sm+ width (`w-[95vw]` + mapped sm:max-w-* cap) so
  // desktop panels without their own width class don't collapse to
  // shrink-to-fit; only the below-sm width changes (flush w-full sheet).
  const widthClasses =
    mobile === "sheet" ? "w-full sm:w-[95vw]" : mobileWidth;

  // Set while the press goes DOWN on the backdrop, read on the click that
  // follows. See `hasDismissOwner` for why the question cannot be asked at
  // click time: by then the child has closed and its marker is gone.
  const pressDismissedAChild = useRef(false);

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    pressDismissedAChild.current =
      e.target === e.currentTarget && hasDismissOwner(e.currentTarget as Element);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // One click, one dismissal. A press on the backdrop while a picker list (or
    // a calendar, or an inline editor) is open belongs to that child: it closes
    // on its own via blur, and tearing the modal down in the same gesture would
    // discard whatever the user had typed into it.
    if (pressDismissedAChild.current) {
      pressDismissedAChild.current = false;
      return;
    }
    if (backdropClosable && e.target === e.currentTarget) {
      onClose();
    }
  };

  const content = (
    // sm:pr-[var(--ai-rail-push)]: center the panel in the app area LEFT of
    // the docked AI drawer rail (340px, z-10000) instead of under it. 0px
    // while the drawer is closed. Every full-viewport overlay needs this —
    // see the floaters using the same var (PBackgroundJobs et al.).
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 bg-black/50 flex ${alignClass} justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${zClass}`}
      onMouseDownCapture={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={preset.initial as TargetAndTransition}
        animate={preset.animate as TargetAndTransition}
        exit={preset.exit as TargetAndTransition}
        transition={preset.transition}
        className={`${widthClasses} ${smSizeClass} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* A rung above the hint rung (`overGuestPopup`) paints its own hints
            above itself — the PInfo default sat under its scrim (audit hop3
            r2 M1). No layer below that: those hints already paint on top. */}
        <TooltipLayer z={hintZAbove(zClass)}>{children}</TooltipLayer>
      </motion.div>
    </motion.div>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
};
